import { useMutation, useQueryClient, queryOptions } from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_ARTICLES,
  ADMIN_ARTICLE_BY_ID,
  ADMIN_AUTHOR_FILES,
  ADMIN_AUTHOR_FILE_BY_ID,
} from "@/lib/graphql/queries/articles";
import {
  ADMIN_CREATE_ARTICLE,
  ADMIN_UPDATE_ARTICLE,
  ADMIN_PUBLISH_ARTICLE,
  ADMIN_UNPUBLISH_ARTICLE,
  ADMIN_DELETE_ARTICLE,
  ADMIN_CREATE_ARTICLE_FROM_FILE,
  ADMIN_UPDATE_ARTICLE_META,
  ADMIN_SYNC_ARTICLE_CONTENT,
} from "@/lib/graphql/mutations/articles";
import type {
  AdminArticleListItem,
  AdminArticleFilters,
  AdminAuthorFileItem,
  AdminAuthorFileDetail,
  Article,
  CreateBlogArticleInput,
  CreateArticleFromFileInput,
  UpdateArticleInput,
  UpdateArticleMetaInput,
} from "@/types/article";
import type { PaginationInput, PaginationMeta } from "@/types/common";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// === Article Query Keys ===

export const articleKeys = {
  all: ["articles"] as const,
  lists: () => [...articleKeys.all, "list"] as const,
  list: (filters: AdminArticleFilters & PaginationInput) =>
    [...articleKeys.lists(), filters] as const,
  details: () => [...articleKeys.all, "detail"] as const,
  detail: (id: string) => [...articleKeys.details(), id] as const,
};

export const authorFileKeys = {
  all: ["authorFiles"] as const,
  lists: () => [...authorFileKeys.all, "list"] as const,
  list: (filters: { page: number; limit: number; search?: string }) =>
    [...authorFileKeys.lists(), filters] as const,
  details: () => [...authorFileKeys.all, "detail"] as const,
  detail: (id: string) => [...authorFileKeys.details(), id] as const,
};

// === Article Query Options ===

interface AdminArticlesResponse {
  items: AdminArticleListItem[];
  meta: PaginationMeta;
}

export function articleListQueryOptions(
  filters: AdminArticleFilters,
  pagination: PaginationInput
) {
  return queryOptions({
    queryKey: articleKeys.list({ ...filters, ...pagination }),
    queryFn: async () => {
      const input = {
        page: pagination.page,
        limit: pagination.limit,
        searchQuery: filters.searchQuery || undefined,
        type: filters.type || undefined,
        publishedOnly: filters.publishedOnly ?? undefined,
      };

      const data = await authenticatedRequest<{
        adminArticles: AdminArticlesResponse;
      }>(ADMIN_ARTICLES, { input });

      return data.adminArticles;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });
}

export function articleDetailQueryOptions(articleId: string) {
  return queryOptions({
    queryKey: articleKeys.detail(articleId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminArticleById: Article;
      }>(ADMIN_ARTICLE_BY_ID, { articleId });

      return data.adminArticleById;
    },
    enabled: !!articleId,
  });
}

// === Author File Query Options ===

interface AdminAuthorFilesResponse {
  items: AdminAuthorFileItem[];
  meta: PaginationMeta;
}

export function authorFileListQueryOptions(
  pagination: PaginationInput,
  search?: string
) {
  return queryOptions({
    queryKey: authorFileKeys.list({ ...pagination, search }),
    queryFn: async () => {
      const input = {
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
      };

      const data = await authenticatedRequest<{
        adminAuthorFiles: AdminAuthorFilesResponse;
      }>(ADMIN_AUTHOR_FILES, { input });

      return data.adminAuthorFiles;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });
}

export function authorFileDetailQueryOptions(fileId: string) {
  return queryOptions({
    queryKey: authorFileKeys.detail(fileId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminAuthorFileById: AdminAuthorFileDetail;
      }>(ADMIN_AUTHOR_FILE_BY_ID, { fileId });

      return data.adminAuthorFileById;
    },
    enabled: !!fileId,
  });
}

// === Article Mutations ===

export function useCreateArticle() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: CreateBlogArticleInput) => {
      const data = await authenticatedRequest<{
        adminCreateArticle: {
          success: boolean;
          article: { _id: string };
        };
      }>(ADMIN_CREATE_ARTICLE, { input });
      return data.adminCreateArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      toast.success("아티클이 생성되었습니다");
      navigate("/articles");
    },
    onError: (error: Error) => {
      toast.error("아티클 생성 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUpdateArticle() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: UpdateArticleInput) => {
      const data = await authenticatedRequest<{
        adminUpdateArticle: { success: boolean; message?: string };
      }>(ADMIN_UPDATE_ARTICLE, { input });
      return data.adminUpdateArticle;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: articleKeys.detail(variables.articleId),
      });
      toast.success("아티클이 수정되었습니다");
      navigate("/articles");
    },
    onError: (error: Error) => {
      toast.error("아티클 수정 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function usePublishArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleId: string) => {
      const data = await authenticatedRequest<{
        adminPublishArticle: { success: boolean; message?: string };
      }>(ADMIN_PUBLISH_ARTICLE, { articleId });
      return data.adminPublishArticle;
    },
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: articleKeys.detail(articleId),
      });
      queryClient.invalidateQueries({ queryKey: authorFileKeys.lists() });
      toast.success("아티클이 발행되었습니다");
    },
    onError: (error: Error) => {
      toast.error("아티클 발행 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUnpublishArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleId: string) => {
      const data = await authenticatedRequest<{
        adminUnpublishArticle: { success: boolean; message?: string };
      }>(ADMIN_UNPUBLISH_ARTICLE, { articleId });
      return data.adminUnpublishArticle;
    },
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: articleKeys.detail(articleId),
      });
      queryClient.invalidateQueries({ queryKey: authorFileKeys.lists() });
      toast.success("아티클 발행이 취소되었습니다");
    },
    onError: (error: Error) => {
      toast.error("발행 취소 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleId: string) => {
      const data = await authenticatedRequest<{
        adminDeleteArticle: { success: boolean; message?: string };
      }>(ADMIN_DELETE_ARTICLE, { articleId });
      return data.adminDeleteArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: authorFileKeys.lists() });
      toast.success("아티클이 삭제되었습니다");
    },
    onError: (error: Error) => {
      toast.error("아티클 삭제 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

// === File-Based Article Mutations ===

export function useCreateArticleFromFile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: CreateArticleFromFileInput) => {
      const data = await authenticatedRequest<{
        adminCreateArticleFromFile: { success: boolean; message?: string };
      }>(ADMIN_CREATE_ARTICLE_FROM_FILE, { input });
      return data.adminCreateArticleFromFile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: authorFileKeys.lists() });
      toast.success("프리미엄 아티클이 생성되었습니다");
      navigate("/articles");
    },
    onError: (error: Error) => {
      toast.error("아티클 생성 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useUpdateArticleMeta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateArticleMetaInput) => {
      const data = await authenticatedRequest<{
        adminUpdateArticleMeta: { success: boolean; message?: string };
      }>(ADMIN_UPDATE_ARTICLE_META, { input });
      return data.adminUpdateArticleMeta;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: articleKeys.detail(variables.articleId),
      });
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      toast.success("아티클 메타정보가 수정되었습니다");
    },
    onError: (error: Error) => {
      toast.error("메타정보 수정 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}

export function useSyncArticleContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articleId: string) => {
      const data = await authenticatedRequest<{
        adminSyncArticleContent: { success: boolean; message?: string };
      }>(ADMIN_SYNC_ARTICLE_CONTENT, { input: { articleId } });
      return data.adminSyncArticleContent;
    },
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({
        queryKey: articleKeys.detail(articleId),
      });
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: authorFileKeys.lists() });
      toast.success("아티클 콘텐츠가 동기화되었습니다");
    },
    onError: (error: Error) => {
      toast.error("콘텐츠 동기화 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}
